import {
  applicationAnimalScreeningConvertor,
  applicationDetailsConvertor,
  applicationVerificationMessageConvertor,
  approveApplicationConverter,
  approvePassedMoveInApplicationConvertor,
  getApplicantAnimalDetailsConverter,
} from 'data/converters/application.converters';
import {
  TAnimalApplicantDetail,
  TAnimalApplicantDetailUi,
  TAnimalDashboard,
  TApplicantAnimalScreeningUI,
  TApplicationActionStatistics,
  TApplicationDetailsDB,
  TApplicationDetailsUI,
  TApproveApplicationDB,
  TApproveApplicationUI,
  TApprovePassedMoveInApplicationDB,
  TApprovePassedMoveInApplicationUI,
  TVerificationMessageDB,
  TVerificationMessageUi,
} from 'data/types/application.types';
import { TAnimalApplicationTargetUI } from 'data/types/animalActions.types';
import { EApplicationUrls } from 'data/urls/application.url';
import { EPropertyMangerUrl } from 'data/urls/propertyManager.url';
import { authSplitApi } from '../helpers/slice.helpers';

const baseUrl = `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EApplicationUrls.APPLICATION}`;

export const applicationApi = authSplitApi('applicationApi', [
  'applicationDetails',
  'applicationVerification',
  'applicantAnimal',
  'applications',
]).injectEndpoints({
  endpoints: (build) => ({
    approveApplication: build.mutation<TApprovePassedMoveInApplicationUI, TApproveApplicationUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EApplicationUrls.APPROVE}`,
          method: 'PATCH',
          body: approveApplicationConverter.toDb(body),
        };
      },
      transformResponse: (data: TApprovePassedMoveInApplicationDB) =>
        approvePassedMoveInApplicationConvertor.fromDb(data),
    }),
    dontApproveAnimalApplicant: build.mutation<void, TAnimalApplicationTargetUI & { rejectReason: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${EApplicationUrls.REJECT}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['applicantAnimal'],
    }),
    getApplicationDetails: build.query<TApplicationDetailsUI, { applicationId: string }>({
      query({ applicationId }) {
        return {
          url: `${baseUrl}/${EApplicationUrls.MOVE_IN}/${EApplicationUrls.DETAILS}`,
          method: 'GET',
          params: { applicationId },
        };
      },
      providesTags: ['applicationDetails'],
      transformResponse: (data: TApplicationDetailsDB) => applicationDetailsConvertor.fromDb(data),
    }),
    getApplicantAnimal: build.query<TAnimalApplicantDetailUi, string>({
      query(id) {
        return {
          url: `${baseUrl}/${EApplicationUrls.ANIMALS}/${id}`,
          method: 'GET',
        };
      },
      providesTags: ['applicantAnimal'],
      transformResponse: (data: TAnimalApplicantDetail) => getApplicantAnimalDetailsConverter.fromDb(data),
    }),
    getApplicationVerification: build.query<TVerificationMessageUi, { applicationId: string; propertyId: string }>({
      query(params) {
        return {
          url: `${baseUrl}/${EApplicationUrls.MOVE_IN}/${EApplicationUrls.VERIFY}`,
          method: 'GET',
          params: { ...params },
        };
      },
      providesTags: ['applicationVerification'],
      transformResponse: (data: TVerificationMessageDB) => applicationVerificationMessageConvertor.fromDb(data),
    }),
    moveIn: build.mutation<TApproveApplicationDB, TApproveApplicationUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EApplicationUrls.MOVE_IN}`,
          method: 'PATCH',
          body: approveApplicationConverter.toDb(body),
        };
      },
    }),
    retractAnimal: build.mutation<string, TAnimalApplicationTargetUI & { reason?: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${EApplicationUrls.RETRACT}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['applicantAnimal'],
    }),
    reApplyAnimal: build.mutation<string, TAnimalApplicationTargetUI & { reason?: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${EApplicationUrls.REAPPLY}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['applicantAnimal'],
    }),
    applicantAnimalScreening: build.mutation<string, TApplicantAnimalScreeningUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EApplicationUrls.SCREENING}`,
          method: 'PATCH',
          body: applicationAnimalScreeningConvertor.toDb(body),
        };
      },
      invalidatesTags: ['applicantAnimal'],
    }),
    // Narrower than its neighbours on purpose: the manager-side command binds an applicationId
    // and nothing else, so this endpoint cannot resolve an animal the way retract and reapply do.
    conditionalApplicantApprove: build.mutation<string, { applicationId: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${EApplicationUrls.CONDITIONAL_APPROVED}/${EApplicationUrls.APPROVE}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['applicantAnimal', 'applicationDetails', 'applications'],
    }),
    getApplicationDashboard: build.query<TAnimalDashboard, void>({
      query() {
        return {
          url: `${baseUrl}/${EApplicationUrls.DASHBOARD}`,
          method: 'GET',
        };
      },
    }),
    getApplicationActionStatistics: build.query<TApplicationActionStatistics, void>({
      query() {
        return {
          url: `${baseUrl}/${EApplicationUrls.ACTION}/${EApplicationUrls.STATISTICS}`,
          method: 'GET',
        };
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useApproveApplicationMutation,
  useGetApplicantAnimalQuery,
  useDontApproveAnimalApplicantMutation,
  useLazyGetApplicationDetailsQuery,
  useGetApplicationDetailsQuery,
  useGetApplicationVerificationQuery,
  useMoveInMutation,
  useRetractAnimalMutation,
  useReApplyAnimalMutation,
  useApplicantAnimalScreeningMutation,
  useConditionalApplicantApproveMutation,
  useGetApplicationDashboardQuery,
  useGetApplicationActionStatisticsQuery,
} = applicationApi;
